import { BackendRoute } from '../../../../config/index';
import { QueryKey } from '../../../../constants/index';
import { useAppQuery } from '../../../../hooks/use-app-query.hook';
import ApiService from '../../../../services/api-service.util';
import { DocumentCategory } from '../../enums/document-category.const';
import { SignatureScopeFilter } from '../enums/signature-scope.const';
import { SignatureSortBy } from '../enums/signature-sort.const';
import { SignatureStatus } from '../enums/signature-status.const';
import { SignatureRow } from '../types/signature-row.types';

type GetSignaturesResponse = {
  count: number;
  records: SignatureRow[];
  totalPages: number;
};

export type GetSignaturesParams = {
  categories?: DocumentCategory[];
  currentPage?: number;
  enabled?: boolean;
  mySignatorySigned?: boolean;
  refetchInterval?: false | number;
  refetchOnWindowFocus?: boolean;
  scope?: SignatureScopeFilter;
  search?: string;
  sortBy?: SignatureSortBy;
  sortOrder?: 'asc' | 'desc';
  statuses?: SignatureStatus[];
  take?: number;
};

type QueryParams = {
  skip: number;
} & Omit<
  GetSignaturesParams,
  'currentPage' | 'enabled' | 'refetchInterval' | 'refetchOnWindowFocus'
>;

const buildQueryParams = (params: QueryParams) => {
  const {
    categories,
    mySignatorySigned,
    scope,
    search,
    sortBy,
    sortOrder,
    statuses,
    skip,
    take = 20,
  } = params;
  return {
    ...(categories?.length ? { categories: categories.join(',') } : {}),
    ...(mySignatorySigned !== undefined ? { mySignatorySigned: String(mySignatorySigned) } : {}),
    ...(scope ? { scope } : {}),
    ...(search ? { search } : {}),
    ...(sortBy ? { sortBy } : {}),
    ...(sortOrder ? { sortOrder } : {}),
    ...(statuses?.length ? { statuses: statuses.join(',') } : {}),
    skip,
    take,
  };
};

export const useSignatures = (params: GetSignaturesParams) => {
  const {
    currentPage = 1,
    enabled = true,
    take = 20,
    refetchInterval,
    refetchOnWindowFocus,
    ...rest
  } = params;
  const skip = (currentPage <= 1 ? 0 : currentPage - 1) * take;
  const queryParams = buildQueryParams({ ...rest, skip, take });

  const { data, isPending } = useAppQuery<GetSignaturesResponse>({
    queryKey: [QueryKey.GET_SIGNATURES, queryParams],
    queryFn: () =>
      ApiService.get<GetSignaturesResponse>({ endpoint: BackendRoute.SIGNATURES }, { queryParams }),
    enabled,
    placeholderData: (previousData) => previousData,
    ...(refetchInterval !== undefined ? { refetchInterval } : {}),
    ...(refetchOnWindowFocus !== undefined ? { refetchOnWindowFocus } : {}),
  });

  const signatures: SignatureRow[] = data?.records ?? [];
  const count = data?.count ?? 0;
  const totalPages = data?.totalPages ?? 0;

  return {
    signatures,
    count,
    totalPages,
    isPending,
  };
};
